import {
  MenuItem, Pagination, Select, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TableSortLabel, Typography,
} from '@mui/material';
import EmptyState from './EmptyState';
import LoadingSkeleton from './LoadingSkeleton';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * DataTable — presentational table with controlled sorting and pagination.
 *
 * Columns: [{ key, label, sortable=true, align, width, headerContent, renderCell(row), renderFilter() }]
 *   - renderCell(row): custom cell renderer; default renders String(row[key]) or '—'
 *   - headerContent: custom header node (overrides label/sort)
 *   - renderFilter(): node for the filter row (shown when showFilterRow)
 *
 * @param {Array}    columns
 * @param {Array}    rows
 * @param {function} [rowKey]        — (row) => key; defaults to row.id
 * @param {function} [onRowClick]    — (row) => void; adds pointer + hover
 * @param {string}   [sortColumn]
 * @param {'asc'|'desc'} [sortDirection]
 * @param {function} [onSort]        — (key) => void
 * @param {boolean}  [showFilterRow]
 * @param {boolean}  [loading]
 * @param {object}   [emptyProps]    — Props passed to EmptyState when rows is empty
 * @param {object}   [pagination]    — { page, pageSize, total, onPageChange, onPageSizeChange, itemLabel }
 * @param {'small'|'medium'} [size='small']
 */
const DataTable = ({
  columns,
  rows,
  rowKey = (row) => row.id,
  onRowClick,
  sortColumn,
  sortDirection = 'asc',
  onSort,
  showFilterRow = false,
  loading = false,
  emptyProps,
  pagination,
  size = 'small',
}) => {
  if (loading) return <LoadingSkeleton variant="table" />;

  if (!rows || rows.length === 0) {
    return <EmptyState compact title="Nothing here yet" {...emptyProps} />;
  }

  const renderHeaderCell = (col) => {
    if (col.headerContent !== undefined) {
      return (
        <TableCell key={col.key} align={col.align} sx={{ width: col.width, whiteSpace: 'nowrap' }}>
          {col.headerContent}
        </TableCell>
      );
    }
    const sortable = onSort && col.sortable !== false;
    return (
      <TableCell
        key={col.key}
        align={col.align}
        sortDirection={sortColumn === col.key ? sortDirection : false}
        sx={{ width: col.width, whiteSpace: 'nowrap' }}
      >
        {sortable ? (
          <TableSortLabel
            active={sortColumn === col.key}
            direction={sortColumn === col.key ? sortDirection : 'asc'}
            onClick={() => onSort(col.key)}
          >
            {col.label}
          </TableSortLabel>
        ) : (
          col.label
        )}
      </TableCell>
    );
  };

  return (
    <>
      <TableContainer>
        <Table size={size}>
          <TableHead>
            <TableRow>{columns.map(renderHeaderCell)}</TableRow>
            {showFilterRow && (
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={`filter-${col.key}`} sx={{ py: 0.5, px: 1 }}>
                    {col.renderFilter ? col.renderFilter() : null}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={rowKey(row)}
                hover={Boolean(onRowClick)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={onRowClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row); } } : undefined}
                sx={onRowClick ? (theme) => ({
                  cursor: 'pointer',
                  '&:focus-visible': {
                    outline: 'none',
                    boxShadow: `inset 0 0 0 2px rgba(${theme.vars.palette.primary.mainChannel} / 0.5)`,
                  },
                }) : undefined}
              >
                {columns.map((col) => (
                  <TableCell key={col.key} align={col.align}>
                    {col.renderCell
                      ? col.renderCell(row)
                      : (row[col.key] != null && String(row[col.key]).trim() !== '' ? String(row[col.key]) : '—')}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && <DataTablePagination {...pagination} />}
    </>
  );
};

export const DataTablePagination = ({ page, pageSize, total, onPageChange, onPageSizeChange, itemLabel = 'rows' }) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safePage = Math.min(page, totalPages);
  const startRow = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const endRow = Math.min(safePage * pageSize, total);

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ px: 2, py: 1.5 }}>
      <Typography variant="body2" color="text.secondary">
        Showing {startRow}–{endRow} of {total} {itemLabel}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
        {onPageSizeChange && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" color="text.secondary">Rows per page:</Typography>
            <Select size="small" value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))} sx={{ minWidth: 72 }}>
              {PAGE_SIZE_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </Stack>
        )}
        {totalPages > 1 && (
          <Pagination count={totalPages} page={safePage} onChange={(_, p) => onPageChange(p)} size="small" shape="rounded" />
        )}
      </Stack>
    </Stack>
  );
};

export default DataTable;
